import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Icon } from '@iconify/react';
import Dropdown from 'bootstrap/js/dist/dropdown';
import AsyncSelect from 'react-select/async';
import Select from 'react-select';
import { create, update, getById } from '../api';
import { createParts, getParts } from '../api/partsAPI';
import { getStockItemses } from '../api/stock-items-quantitiesAPI';
import {
  createPurchaseOrderLine,
  deletePurchaseOrderLine,
  getPurchaseOrderLinesByPurchaseOrderId,
} from '../api/purchase-order-linesAPI';
import {
  createPurchaseOrder,
  updatePurchaseOrder,
  getPurchaseOrderById,
  getPaginatedPurchaseOrders,
} from '../api/purchase-orderAPI'
import { createStockItems, getWarehouseStockForWorkOrder } from '../api/stock-itemsAPI';
import { createStockItemsQuantities } from '../api/stock-items-quantitiesAPI';
import { getHierarchyStructureById } from '../api/hierarchy-structureAPI';
import AddPartModal from './parts/AddPartModal';
import AddPartsModal from './parts/AddPartsModal';
import PurchaseOrderDetails from './PurchaseOrderDetails';
import PurchaseOrderPrintView from './PurchaseOrderPrintView';
import { getManufacturer } from '../api/manufacturerAPI';
import { getSupplierById, getSuppliers } from '../api/supplierAPI';
import { createBillOfMaterialsWithAi } from '../api/ai-bill-of-materialsAPI';
import { getSupplierForDepartment } from '../api/department-suppliersAPI';
import { loadSupplierOptions } from '../utils/supplierOptionUtils';
import { useFormBuilder } from '../utils/useFormBuilder';
import { validateForm } from '../utils/useValidator';

const PARTS_PAGE_SIZE = 25;
const DEFAULT_PART_CATEGORY_ID = 25;

export const saveWorkOrderParts = async (editingId, workOrderParts) => {
  try {
    const updatedParts = await Promise.all(
      workOrderParts.map(async (p) => {
        const { _clientId, ...payload } = p || {};
        if (p.id) {
          await update('work-order-parts', p.id, {
            workOrderId: editingId,
            partsId: p.partsId,
            quantity: p.quantity,
            source: p.source,
            status: p.status,
            available: p.available ?? 0,
            supplierId: p.supplierId || null,
            stockItemId: p.stockItemId ?? p?.parts?.stockItemId ?? null,
            cost: parseFloat(p.cost || 0),
            expenseCodeId: p.expenseCodeId ?? p.expenseCode?.id ?? null,
          });
          return p;
        } else {
          const newPart = await create('work-order-parts', {
            ...payload,
            workOrderId: editingId,
            stockItemId: p.stockItemId ?? p?.parts?.stockItemId ?? null,
            cost: parseFloat(p.cost || 0),
            expenseCodeId: p.expenseCodeId ?? p.expenseCode?.id ?? null,
            available: p.available ?? 0,
          });
          return { ...p, id: newPart.id };
        }
      })
    );
    return updatedParts;
  } catch (err) {
    console.error('Error saving work order parts:', err);
    return [];
  }
};

const WorkOrderPartsTab = ({
  workOrderParts,
  setWorkOrderParts,
  setDeletedPartIds,
  newWorkOrder,
  editingId,
  userProfile,
  expenseOptions,
  defaultPartsExpenseCode,
}) => {
  const [partsErrors, setPartsErrors] = useState({});
  const [selectedPartOption, setSelectedPartOption] = useState(null);
  const [showAddWorkOrderPartModal, setShowAddWorkOrderPartModal] = useState(false);
  const [showAddPartModal, setShowAddPartModal] = useState(false);
  const [showMissingSupplierModal, setShowMissingSupplierModal] = useState(false);
  const [showCannotDeleteModal, setShowCannotDeleteModal] = useState(false);
  const [showReviewPartOrdersModal, setShowReviewPartOrdersModal] = useState(false);
  const [proposedTransactions, setProposedTransactions] = useState([]);
  const [stockLookupByPartId, setStockLookupByPartId] = useState({});
  const [showPurchaseOrderDetailsModal, setShowPurchaseOrderDetailsModal] = useState(false);
  const [purchaseOrderDetails, setPurchaseOrderDetails] = useState(null);
  const [purchaseOrderDetailLines, setPurchaseOrderDetailLines] = useState([]);
  const [isLoadingPurchaseOrderDetails, setIsLoadingPurchaseOrderDetails] = useState(false);
  const [addPartInitialValues, setAddPartInitialValues] = useState({});
  const [showBomAiModal, setShowBomAiModal] = useState(false);
  const [bomAiText, setBomAiText] = useState('');
  const [bomAiError, setBomAiError] = useState('');
  const [bomAiLoading, setBomAiLoading] = useState(false);
  const [showMissingBomModal, setShowMissingBomModal] = useState(false);
  const [missingBomParts, setMissingBomParts] = useState([]);
  const [missingBomError, setMissingBomError] = useState('');
  const [missingBomSaving, setMissingBomSaving] = useState(false);
  const [bomManufacturerOptions, setBomManufacturerOptions] = useState([]);
  const [bomSupplierOptions, setBomSupplierOptions] = useState([]);
  const [bomGlobalSourceId, setBomGlobalSourceId] = useState('');
  const [bomGlobalSupplierId, setBomGlobalSupplierId] = useState('');
  const [bomApplySourceToAll, setBomApplySourceToAll] = useState(false);
  const [bomApplySupplierToAll, setBomApplySupplierToAll] = useState(false);
  const [partForm, setPartForm] = useState({
    partsId: null,
    parts: null,
    quantity: 1,
    source: '',
    cost: '',
  });
  const [partModalErrors, setPartModalErrors] = useState({});
  const [partSearchText, setPartSearchText] = useState('');
  const [partSelectRefreshKey, setPartSelectRefreshKey] = useState(0);
  const [supplierOptions, setSupplierOptions] = useState([]);
  const [supplierSearchState, setSupplierSearchState] = useState({ term: '', page: 1, hasMore: true });
  const [supplierLoading, setSupplierLoading] = useState(false);
  const supplierRequestRef = useRef(0);
  const supplierCacheRef = useRef(new Map());
  const processedPartsRef = useRef(new Set());
  const nextClientIdRef = useRef(0);
  const addNewPartOption = useMemo(
    () => ({ value: 'add-new', label: '+ Add New Part', isAddNew: true }),
    [],
  );
  const isWorkOrderLocked = useMemo(
    () => [4, 5].includes(Number(newWorkOrder?.statusId)),
    [newWorkOrder?.statusId],
  );
  const expenseCodeOptions = useMemo(
    () =>
      Array.isArray(expenseOptions)
        ? expenseOptions.map((ec) => ({ value: ec.id, label: ec.name }))
        : [],
    [expenseOptions],
  );
  const menuPortalStyles = useMemo(
    () => ({
      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    }),
    [],
  );
  const [selectedExpenseCodeOption, setSelectedExpenseCodeOption] = useState(null);
  const selectedExpenseCodeId = useMemo(() => {
    if (!selectedExpenseCodeOption) return null;
    const value = Number(selectedExpenseCodeOption.value);
    return Number.isNaN(value) ? null : value;
  }, [selectedExpenseCodeOption]);

  const resolveSupplierDetails = useCallback(
    async (supplierId) => {
      if (!supplierId) return null;
      if (supplierCacheRef.current.has(supplierId)) {
        return supplierCacheRef.current.get(supplierId);
      }
      try {
        const supplier = await getSupplierById(supplierId);
        if (supplier) {
          supplierCacheRef.current.set(supplierId, supplier);
          return supplier;
        }
      } catch (error) {
        console.error('Error loading supplier details:', error);
      }
      return null;
    },
    [],
  );

  const resolveDepartmentSupplierId = useCallback(
    async (sourceOfSupplyId) => {
      const departmentId = newWorkOrder?.departmentId ?? null;
      if (!departmentId || !sourceOfSupplyId) return null;
      try {
        const response = await getSupplierForDepartment({
          departmentId,
          sourceOfSupplyId,
        });
        return typeof response === 'number' ? response : response?.supplierId ?? null;
      } catch (error) {
        console.error('Error resolving department supplier:', error);
        return null;
      }
    },
    [newWorkOrder?.departmentId],
  );

  const resolvePartSource = useCallback((part) => {
    if (!part) return 'PURCHASE ORDER';
    const stockItemId = part?.stockItemId ?? part?.stockItem?.id ?? part?.stockInfo?.stockItemId ?? null;
    const hasStockItem = stockItemId !== null && stockItemId !== undefined && stockItemId !== '';
    return hasStockItem ? 'WAREHOUSE' : 'PURCHASE ORDER';
  }, []);

  const getSourceOfSupplyId = useCallback((part) => {
    if (!part) return null;
    return (
      part?.parts?.sourceOfSupplyId ??
      part?.sourceOfSupplyId ??
      part?.parts?.sourceOfSupply?.id ??
      null
    );
  }, []);

  const applyDepartmentSupplierToPart = useCallback(
    async (part, { force = false } = {}) => {
      if (!part || part.status !== 'Draft') return part;
      const sourceOfSupplyId = getSourceOfSupplyId(part);
      if (!sourceOfSupplyId) return part;

      const fallbackSupplierId = part?.parts?.supplierId ?? part?.supplierId ?? null;
      const shouldUpdate =
        force || part?.supplierId == null || part?.supplierId === fallbackSupplierId;

      if (!shouldUpdate) {
        return part;
      }

      const departmentSupplierId = await resolveDepartmentSupplierId(sourceOfSupplyId);
      const resolvedSupplierId = departmentSupplierId ?? fallbackSupplierId ?? null;

      if (!resolvedSupplierId) {
        return part;
      }

      let supplier = part?.supplier ?? null;
      if (!supplier || Number(supplier.id) !== Number(resolvedSupplierId)) {
        supplier = await resolveSupplierDetails(resolvedSupplierId);
      }

      return {
        ...part,
        supplierId: resolvedSupplierId,
        supplier,
      };
    },
    [getSourceOfSupplyId, resolveDepartmentSupplierId, resolveSupplierDetails],
  );

  useEffect(() => {
    processedPartsRef.current.clear();
    nextClientIdRef.current = 0;
  }, [newWorkOrder?.id]);

  const ensurePartClientId = useCallback((part) => {
    if (!part || part.id || part._clientId) return part;
    const clientId = `local-${++nextClientIdRef.current}`;
    return { ...part, _clientId: clientId };
  }, []);

  const getPartKeys = useCallback((part) => {
    const keys = [];
    if (part?.id != null) {
      keys.push(part.id);
    }
    if (part?._clientId != null) {
      keys.push(part._clientId);
    }
    return keys;
  }, []);

  const isPartProcessed = useCallback(
    (part) => {
      const keys = getPartKeys(part);
      if (keys.length === 0) return false;
      return keys.some((key) => processedPartsRef.current.has(key));
    },
    [getPartKeys],
  );

  const migrateProcessedKey = useCallback(
    (part) => {
      if (!part?.id || !part?._clientId) return;
      if (processedPartsRef.current.has(part._clientId)) {
        processedPartsRef.current.add(part.id);
      }
    },
    [],
  );

  const markPartProcessed = useCallback((part) => {
    if (!part) return;
    const keys = getPartKeys(part);
    keys.forEach((key) => {
      processedPartsRef.current.add(key);
    });
  }, [getPartKeys]);

  useEffect(() => {
    if (!Array.isArray(workOrderParts)) return;
    workOrderParts.forEach((part) => {
      migrateProcessedKey(part);
    });
  }, [migrateProcessedKey, workOrderParts]);

  useEffect(() => {
    if (!Array.isArray(workOrderParts) || workOrderParts.length === 0) return;

    const hasUnprocessed = workOrderParts.some((part) => !isPartProcessed(part));

    if (!hasUnprocessed) return;

    let isMounted = true;

    const applySuppliersOnce = async () => {
      const updatedParts = await Promise.all(
        workOrderParts.map(async (part) => {
          if (!part) return part;
          let nextPart = ensurePartClientId(part);
          migrateProcessedKey(nextPart);
          if (isPartProcessed(nextPart)) {
            return nextPart;
          }
          const enriched = await applyDepartmentSupplierToPart(nextPart);
          const shouldMarkProcessed =
            (enriched?.status ?? nextPart?.status) !== 'Draft' ||
            getSourceOfSupplyId(enriched) != null ||
            enriched?.supplierId != null;
          if (shouldMarkProcessed) {
            markPartProcessed(enriched);
          }
          return enriched;
        }),
      );

      if (!isMounted) return;

      const hasChanges = updatedParts.some((part, index) => part !== workOrderParts[index]);
      if (hasChanges) {
        setWorkOrderParts(updatedParts);
      }
    };

    applySuppliersOnce();

    return () => {
      isMounted = false;
    }
  }, [
    applyDepartmentSupplierToPart,
    ensurePartClientId,
    isPartProcessed,
    getSourceOfSupplyId,
    markPartProcessed,
    migrateProcessedKey,
    setWorkOrderParts,
    workOrderParts,
  ]);

  useEffect(() => {
    if (!showMissingBomModal) return;
    let isMounted = true;

    const loadBomOptions = async () => {
      try {
        const [manufacturers, suppliers] = await Promise.all([
          getManufacturer(),
          getSuppliers({ page: 1, pageSize: 200 }),
        ]);
        if (!isMounted) return;
        const manufacturerList = Array.isArray(manufacturers)
          ? manufacturers
          : Array.isArray(manufacturers?.data)
            ? manufacturers.data
            : [];
        const supplierList = Array.isArray(suppliers?.data)
          ? suppliers.data
          : Array.isArray(suppliers)
            ? suppliers
            : [];
        setBomManufacturerOptions(manufacturerList);
        setBomSupplierOptions(supplierList);
      } catch (error) {
        console.error('Error loading BOM supplier data:', error);
      }
    };

    loadBomOptions();

    return () => {
      isMounted = false;
    };
  }, [showMissingBomModal]);

  useEffect(() => {
    if (!expenseCodeOptions.length) {
      setSelectedExpenseCodeOption(null);
      return;
    }

    setSelectedExpenseCodeOption((prev) => {
      if (prev && expenseCodeOptions.some((opt) => Number(opt.value) === Number(prev.value))) {
        return prev;
      }

      const defaultValue = defaultPartsExpenseCode ? Number(defaultPartsExpenseCode) : null;
      const fallback =
        expenseCodeOptions.find((opt) => Number(opt.value) === defaultValue) ||
        expenseCodeOptions[0] ||
        null;
      return fallback;
    });
  }, [defaultPartsExpenseCode, expenseCodeOptions]);

  const handlePartSearch = useCallback(
    async (inputValue = '', { page } = {}) => {
      const raw = typeof inputValue === 'string' ? inputValue : inputValue?.search ?? '';
      const trimmed = raw.trim();
      setPartSearchText(trimmed);
      const pageNumber = typeof page === 'number' && page > 0 ? page : 1;
      const shouldLoadDefault = trimmed === '';

      if (!shouldLoadDefault && trimmed.length < 2) {
        return { options: [addNewPartOption], hasMore: false };
      }

      try {
        const resp = await getParts({
          page: pageNumber,
          pageSize: PARTS_PAGE_SIZE,
          search: shouldLoadDefault ? '' : trimmed,
        });
        const items =
          Array.isArray(resp?.data)
            ? resp.data
            : Array.isArray(resp)
              ? resp
              : [];
        const options = items.map((item) => ({
          value: item.id,
          label: `${item.partNumber} – ${item.description}`.trim(),
          raw: item,
        }));
        const optionsWithAddNew = pageNumber === 1 ? [addNewPartOption, ...options] : options;
        const totalCount =
          typeof resp?.total === 'number'
            ? resp.total
            : typeof resp?.count === 'number'
              ? resp.count
              : typeof resp?.meta?.total === 'number'
                ? resp.meta.total
                : undefined;
        const hasMore =
          typeof resp?.hasMore === 'boolean'
            ? resp.hasMore
            : typeof totalCount === 'number'
              ? pageNumber * PARTS_PAGE_SIZE < totalCount
              : items.length === PARTS_PAGE_SIZE;

        return {
          options: optionsWithAddNew,
          totalCount,
          pageSize: PARTS_PAGE_SIZE,
          hasMore,
        };
      } catch (err) {
        console.error('Error fetching parts:', err);
        return { options: [addNewPartOption], hasMore: false };
      }
    },
    [addNewPartOption],
  );

  const handleSupplierSearch = useCallback(loadSupplierOptions, []);
  const mergeSupplierOptions = useCallback((existing = [], incoming = []) => {
    const result = [];
    const seen = new Map();

    const pushOption = (option) => {
      if (!option) return;
      const key = option.value;
      if (seen.has(key)) {
        result[seen.get(key)] = option;
        return;
      }
      seen.set(key, result.length);
      result.push(option);
    };

    existing.forEach(pushOption);
    incoming.forEach(pushOption);
    return result;
  }, []);
  const fetchSupplierOptions = useCallback(
    async (searchTerm = '', page = 1) => {
      const requestId = ++supplierRequestRef.current;
      setSupplierLoading(true);
      try {
        const result = await loadSupplierOptions(searchTerm, { page });
        if (requestId !== supplierRequestRef.current) {
          return Array.isArray(result) ? result : result?.options ?? [];
        }
        const options = Array.isArray(result) ? result : result?.options ?? [];
        setSupplierOptions((prev) =>
          page === 1 ? options : mergeSupplierOptions(prev, options),
        );
        setSupplierSearchState({
          term: searchTerm,
          page,
          hasMore: result?.hasMore ?? false,
        });
        return options;
      } catch (err) {
        console.error('Error fetching suppliers:', err);
        return [];
      } finally {
        if (requestId === supplierRequestRef.current) {
          setSupplierLoading(false);
        }
      }
    },
    [mergeSupplierOptions],
  );
  const handleAsyncSupplierSearch = useCallback(
    (inputValue) => {
      const term = typeof inputValue === 'string' ? inputValue.trim() : '';
      return fetchSupplierOptions(term, 1);
    },
    [fetchSupplierOptions],
  );
  const handleSupplierMenuScroll = useCallback(() => {
    if (supplierLoading || !supplierSearchState.hasMore) return;
    const nextPage = (supplierSearchState.page || 1) + 1;
    fetchSupplierOptions(supplierSearchState.term, nextPage);
  }, [fetchSupplierOptions, supplierLoading, supplierSearchState]);

  const formatPartOptionLabel = useCallback((option) => {
    if (option?.isAddNew) {
      return option.label;
    }

    const part = option?.raw ?? null;
    const hasStockItem = part?.stockItemId !== null && part?.stockItemId !== undefined;

    return (
      <div className="d-flex align-items-center gap-2">
        <span>{option?.label}</span>
        {hasStockItem ? <span className="badge bg-secondary ms-auto">Stock Item</span> : null}
      </div>
    );
  }, []);

  useEffect(() => {
    fetchSupplierOptions('', 1);
  }, [fetchSupplierOptions]);

  const partModalFields = useMemo(
    () => [
      {
        field: 'partsId',
        label: 'Part',
        type: 'paginated-select',
        size: 12,
        loadOptions: handlePartSearch,
        selectedOption: selectedPartOption,
        setSelectedOption: setSelectedPartOption,
        required: true,
        isClearable: true,
        formatOptionLabel: formatPartOptionLabel,
        onChange: (option, setFormData, field, setSelectedOptionFn) => {
          if (option?.isAddNew) {
            if (setSelectedOptionFn) {
              setSelectedOptionFn(null);
            }
            setAddPartInitialValues((prev) => ({
              ...prev,
              categoryId: prev.categoryId ?? 25,
              partNumber: partSearchText,
            }));
            setShowAddPartModal(true);
            return;
          }

          if (setSelectedOptionFn) {
            setSelectedOptionFn(option ?? null);
          }

          setFormData((prev) => {
            const next = {
              ...prev,
              [field]: option?.value ?? null,
              partsId: option?.value ?? null,
              parts: option?.raw ?? null,
            };

            if (option && option.raw) {
              const resolvedSource = resolvePartSource(option.raw);
              const resolvedCost =
                option.raw.lastPrice ?? option.raw.partsCost ?? option.raw.partsPrice ?? '';
              next.cost = resolvedCost;
              next.source = resolvedSource;
              next.supplierId = option.raw.supplierId ?? null;
              next.supplier = option.raw.supplier ?? null;
            } else if (!option) {
              next.parts = null;
              next.cost = '';
              next.source = '';
              next.supplierId = null;
              next.supplier = null;
            }

            return next;
          });
        },
      },
      {
        field: 'quantity',
        label: 'Quantity',
        type: 'number',
        size: 6,
        required: true,
        // mustBePositiveInteger: true,
      },
      { field: 'cost', label: 'Cost', type: 'number', size: 6 },
      /* {
        field: 'supplierId',
        label: 'Supplier',
        type: 'paginated-select',
        size: 12,
        loadOptions: handleSupplierSearch,
        selectedOption: partForm.supplier
          ? { value: partForm.supplier.id ?? partForm.supplierId, label: partForm.supplier.name }
          : null,
        setSelectedOption: (option) => {
          setPartForm((prev) => ({
            ...prev,
            supplierId: option?.value ?? null,
            supplier: option?.raw ?? null,
          }));
        },
        required: false,
        isClearable: true,
        minSearchLength: 2,
      },*/

    ],
    [
      handlePartSearch,
      handleSupplierSearch,
      partForm.supplier,
      partForm.supplierId,
      partSearchText,
      formatPartOptionLabel,
      resolvePartSource,
      selectedPartOption,
    ],
  );

  const { renderField: renderPartModalField } = useFormBuilder(
    partModalFields,
    partForm,
    setPartForm,
    partModalErrors,
    { refreshKey: partSelectRefreshKey },
  );

  const resetPartModal = () => {
    setShowAddWorkOrderPartModal(false);
    setPartModalErrors({});
    setPartForm({
      partsId: null,
      parts: null,
      quantity: 1,
      source: '',
      cost: '',
    });
    setSelectedPartOption(null);
    setPartSearchText('');
    setPartSelectRefreshKey((prev) => prev + 1);
  };

  const normalizeBomOptional = (value) => {
    if (value === null || value === undefined) return 0;
    if (typeof value === 'boolean') return value ? 1 : 0;
    if (typeof value === 'number') return value ? 1 : 0;
    if (typeof value === 'string') {
      const trimmed = value.trim().toLowerCase();
      if (['1', 'true', 'yes', 'y'].includes(trimmed)) return 1;
      if (['0', 'false', 'no', 'n'].includes(trimmed)) return 0;
    }
    return 0;
  };

  const normalizeBomQuantity = (value) => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed) || parsed < 0) return 1;
    return Math.trunc(parsed);
  };

  const normalizeBomPrice = (value) => {
    if (value === null || value === undefined || value === '') return 0;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const normalizeBomPricingDate = (value) => {
    if (!value) return new Date().toISOString();
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? new Date().toISOString() : parsed.toISOString();
  };

  const findExistingPartByNumber = async (partNumber) => {
    const trimmed = typeof partNumber === 'string' ? partNumber.trim() : '';
    if (!trimmed) return null;
    const response = await getParts({ search: trimmed, page: 1, pageSize: 25 });
    const items = Array.isArray(response?.data)
      ? response.data
      : Array.isArray(response)
        ? response
        : [];
    const lower = trimmed.toLowerCase();
    return items.find((item) => item.partNumber?.toLowerCase() === lower) || null;
  };

  const openBomAiModal = () => {
    setBomAiText('');
    setBomAiError('');
    setShowBomAiModal(true);
  };

  const closeBomAiModal = () => {
    if (bomAiLoading) return;
    setShowBomAiModal(false);
    setBomAiText('');
    setBomAiError('');
  };

  const handleImportBomWithAi = async () => {
    if (bomAiLoading) return;
    if (!bomAiText.trim()) {
      setBomAiError('Please provide bill of materials details.');
      return;
    }
    setBomAiLoading(true);
    setBomAiError('');

    try {
      const response = await createBillOfMaterialsWithAi(bomAiText.trim());
      if (response?.message) {
        setBomAiError(response.message);
        return;
      }

      const aiParts = Array.isArray(response?.data) ? response.data : null;
      if (!aiParts) {
        setBomAiError('Unable to parse AI response.');
        return;
      }

      const normalizedParts = aiParts
        .map((part) => ({
          partNumber: typeof part.partNumber === 'string' ? part.partNumber.trim() : '',
          partDescription: typeof part.partDescription === 'string' ? part.partDescription.trim() : '',
          quantity: normalizeBomQuantity(part.quantity),
          partsPrice: normalizeBomPrice(part.partsPrice),
          pricingDate: normalizeBomPricingDate(part.pricingDate),
          optional: normalizeBomOptional(part.optional),
        }))
        .filter((part) => part.partNumber);

      const lookupResults = await Promise.all(
        normalizedParts.map(async (part) => ({
          part,
          existingPart: await findExistingPartByNumber(part.partNumber),
        })),
      );

      const matchedExpense = Array.isArray(expenseOptions)
        ? expenseOptions.find((ec) => Number(ec.id) === Number(selectedExpenseCodeId))
        : null;
      const expenseCodePayload = matchedExpense ? { ...matchedExpense } : null;

      const existingWorkOrderParts = lookupResults
        .filter((result) => result.existingPart)
        .map(({ part, existingPart }) => {
          const resolvedSource = resolvePartSource(existingPart);
          const resolvedSupplierId = resolvedSource === 'PURCHASE ORDER' ? existingPart.supplierId ?? null : null;
          const resolvedSupplier = resolvedSource === 'PURCHASE ORDER' ? existingPart.supplier ?? null : null;
          return {
            id: null,
            workOrderId: newWorkOrder.id || null,
            partsId: existingPart.id,
            quantity: part.quantity,
            supplierId: resolvedSupplierId,
            supplier: resolvedSupplier,
            source: resolvedSource,
            status: 'Draft',
            parts: existingPart,
            expenseCodeId: selectedExpenseCodeId ?? null,
            expenseCode: expenseCodePayload,
            cost:
              part.partsPrice !== undefined && part.partsPrice !== null
                ? part.partsPrice
                : existingPart.lastPrice ?? 0,
          };
        });

      const missingParts = lookupResults
        .filter((result) => !result.existingPart)
        .map(({ part }) => ({
          ...part,
          sourceOfSupplyId: '',
          supplierId: '',
        }));

      if (existingWorkOrderParts.length > 0) {
        const enrichedParts = await Promise.all(
          existingWorkOrderParts.map((part) => {
            const withClientId = ensurePartClientId(part);
            return applyDepartmentSupplierToPart(withClientId);
          }),
        );
        enrichedParts.forEach(markPartProcessed);
        setWorkOrderParts((prev) => [...prev, ...enrichedParts]);
      }

      if (missingParts.length > 0) {
        setMissingBomParts(missingParts);
        setShowMissingBomModal(true);
        setMissingBomError('');
        setBomApplySourceToAll(false);
        setBomApplySupplierToAll(false);
        setBomGlobalSourceId('');
        setBomGlobalSupplierId('');
      }

      setShowBomAiModal(false);
      setBomAiText('');
    } catch (error) {
      console.error('Error importing BOM with AI:', error);
      setBomAiError('Unable to create bill of materials with AI.');
    } finally {
      setBomAiLoading(false);
    }
  };

  const handleMissingBomUpdate = (index, updates) => {
    setMissingBomParts((prev) =>
      prev.map((part, i) => (i === index ? { ...part, ...updates } : part)),
    );
  };

  const handleGlobalBomSourceChange = (value) => {
    setBomGlobalSourceId(value);
    if (bomApplySourceToAll) {
      setMissingBomParts((prev) =>
        prev.map((part) => ({ ...part, sourceOfSupplyId: value })),
      );
    }
  };

  const handleGlobalBomSupplierChange = (value) => {
    setBomGlobalSupplierId(value);
    if (bomApplySupplierToAll) {
      setMissingBomParts((prev) =>
        prev.map((part) => ({ ...part, supplierId: value })),
      );
    }
  };

  const handleApplySourceToAllChange = (checked) => {
    setBomApplySourceToAll(checked);
    if (checked) {
      setMissingBomParts((prev) =>
        prev.map((part) => ({ ...part, sourceOfSupplyId: bomGlobalSourceId })),
      );
    }
  };

  const handleApplySupplierToAllChange = (checked) => {
    setBomApplySupplierToAll(checked);
    if (checked) {
      setMissingBomParts((prev) =>
        prev.map((part) => ({ ...part, supplierId: bomGlobalSupplierId })),
      );
    }
  };

  const handleSaveMissingBomParts = async () => {
    if (missingBomSaving) return;
    const incomplete = missingBomParts.some(
      (part) => !part.sourceOfSupplyId || !part.supplierId,
    );
    if (incomplete) {
      setMissingBomError(
        'Please select a Source of Supply and Preferred Supplier for all parts.',
      );
      return;
    }

    setMissingBomSaving(true);
    setMissingBomError('');

    try {
      const createdParts = await Promise.all(
        missingBomParts.map(async (part) =>
          createParts({
            partNumber: part.partNumber,
            description: part.partDescription,
            sourceOfSupplyId: Number(part.sourceOfSupplyId),
            supplierId: Number(part.supplierId),
            categoryId: DEFAULT_PART_CATEGORY_ID,
            lastPrice: part.partsPrice ?? '',
          }),
        ),
      );

      const matchedExpense = Array.isArray(expenseOptions)
        ? expenseOptions.find((ec) => Number(ec.id) === Number(selectedExpenseCodeId))
        : null;
      const expenseCodePayload = matchedExpense ? { ...matchedExpense } : null;

      const newWorkOrderParts = createdParts.map((created, index) => {
        const aiPart = missingBomParts[index];
        const resolvedSource = resolvePartSource(created);
        const resolvedSupplierId = resolvedSource === 'PURCHASE ORDER' ? created.supplierId ?? null : null;
        const resolvedSupplier = resolvedSource === 'PURCHASE ORDER' ? created.supplier ?? null : null;
        return {
          id: null,
          workOrderId: newWorkOrder.id || null,
          partsId: created.id,
          quantity: aiPart.quantity,
          supplierId: resolvedSupplierId,
          supplier: resolvedSupplier,
          source: resolvedSource,
          status: 'Draft',
          available: 0,
          parts: created,
          expenseCodeId: selectedExpenseCodeId ?? null,
          expenseCode: expenseCodePayload,
          cost:
            aiPart.partsPrice !== undefined && aiPart.partsPrice !== null
              ? aiPart.partsPrice
              : created.lastPrice ?? 0,
        };
      });

      const enrichedParts = await Promise.all(
        newWorkOrderParts.map((part) => {
          const withClientId = ensurePartClientId(part);
          return applyDepartmentSupplierToPart(withClientId);
        }),
      );
      enrichedParts.forEach(markPartProcessed);
      setWorkOrderParts((prev) => [...prev, ...enrichedParts]);

      setShowMissingBomModal(false);
      setMissingBomParts([]);
      setMissingBomError('');
    } catch (error) {
      console.error('Error saving BOM parts:', error);
      setMissingBomError('Unable to add new parts. Please try again.');
    } finally {
      setMissingBomSaving(false);
    }
  };

  const handlePartModalSave = async () => {
    const errs = validateForm(partModalFields, partForm);
    setPartModalErrors(errs);
    if (Object.keys(errs).length) return;

    const part = partForm.parts;
    if (!part) return;

    const source = resolvePartSource(part);
    const quantityValue = Number(partForm.quantity) || 1;
    const costValue = Number.isFinite(Number(partForm.cost)) ? Number(partForm.cost) : 0;
    const resolvedSupplierId = source === 'PURCHASE ORDER' ? partForm.supplierId ?? part.supplierId ?? null : null;
    const resolvedSupplier = source === 'PURCHASE ORDER' ? partForm.supplier ?? part.supplier ?? null : null;
    const matchedExpense = Array.isArray(expenseOptions)
      ? expenseOptions.find((ec) => Number(ec.id) === Number(selectedExpenseCodeId))
      : null;
    const expenseCodePayload = matchedExpense ? { ...matchedExpense } : null;

    const newPart = {
      id: null,
      workOrderId: newWorkOrder.id || null,
      partsId: part.id,
      quantity: quantityValue,
      supplierId: resolvedSupplierId,
      supplier: resolvedSupplier,
      source,
      status: 'Draft',
      available: 0,
      parts: part,
      stockItemId: part.stockItemId ?? null,
      expenseCodeId: selectedExpenseCodeId ?? null,
      expenseCode: expenseCodePayload,
      cost: costValue,
    };

    const enrichedPart = await applyDepartmentSupplierToPart(ensurePartClientId(newPart));
    markPartProcessed(enrichedPart);

    setWorkOrderParts((prev) => [
      ...prev,
      {
        ...enrichedPart,
        expenseCodeId: selectedExpenseCodeId ?? null,
        expenseCode: expenseCodePayload,
      },
    ]);

    resetPartModal();
  };

  /*const handleConfirmMaterials = async () => {
    if (!editingId) return;
    try {
      const stockList = await getStockItemses();
      const stockMap = {};
      stockList.forEach((s) => {
        stockMap[s.partId] = s;
      });
      setWorkOrderParts((prev) =>
        prev.map((p) => {
          if (p.source === 'WAREHOUSE') {
            const info = stockMap[p.partsId];
            const soh = info ? Number(info.stockOnHand) : 0;
            return {
              ...p,
              stockInfo: {
                stockItemId: info?.stockItemId,
                soh,
                available: soh,
                backOrder: info?.backOrder || 0,
                reserved: info?.reserved || 0,
                inStock: soh >= (p.quantity || 0),
              },
            };
          }
          return { ...p, stockInfo: undefined };
        })
      );
    } catch (err) {
      console.error('Error checking stock', err);
    }
  };*/

  const resolvePurchaseOrderStatus = (po) => {
    const statusValue = po?.status?.status;
    if (!statusValue) return 'PendingApproval';
    if (statusValue.toLowerCase() === 'backorder') return 'Backorder';
    return statusValue;
  };

  const resolvePurchaseOrderStatusId = (order) => {
    const rawCandidates = [order?.statusId, order?.status?.id, order?.status?.statusId, order?.status];
    for (const value of rawCandidates) {
      if (value === null || value === undefined) continue;
      const numeric = Number(value);
      if (!Number.isNaN(numeric)) return numeric;
    }
    const statusLabel = (order?.status?.status || order?.status || '').toString().toLowerCase();
    if (!statusLabel) return null;
    const map = {
      draft: 1,
      approved: 2,
      sent: 3,
      invoiced: 4,
      cancelled: 5,
    };
    return map[statusLabel] ?? null;
  };

  const findOpenPurchaseOrder = useCallback(async (supplierId, departmentId) => {
    if (!supplierId || !departmentId) return null;
    try {
      const resp = await getPaginatedPurchaseOrders({ supplierId, pageSize: 25 });
      const orders = Array.isArray(resp?.data) ? resp.data : Array.isArray(resp) ? resp : [];
      return orders.find((order) => {
        const statusId = resolvePurchaseOrderStatusId(order);
        const orderDepartmentId = Number(order?.departmentId ?? order?.department?.id);
        return statusId === 1 && orderDepartmentId === Number(departmentId);
      }) || null;
    } catch (err) {
      console.error('Error loading purchase orders', err);
      return null;
    }
  }, []);

  const handleRequisitionParts = async ({ ignoreMissingSuppliers = false } = {}) => {
    const newErrors = {};
    const missingSuppliers = [];
    workOrderParts.forEach((p, idx) => {
      if (p.status === 'Draft') {
        if (p.source === 'PURCHASE ORDER' && !p.supplierId) {
          missingSuppliers.push({ part: p, index: idx });
        } else if (p.cost !== undefined && !/^\d+(\.\d{1,2})?$/.test(String(p.cost))) {
          newErrors[idx] = { cost: 'Invalid cost' };
        }
      }
    });
    setPartsErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    if (missingSuppliers.length > 0 && !ignoreMissingSuppliers) {
      setShowMissingSupplierModal(true);
      return;
    }

    const updatedParts = await saveWorkOrderParts(editingId, workOrderParts);
    setWorkOrderParts(updatedParts);

    const today = new Date();
    const format = (d) => d.toISOString().slice(0, 10);
    const priority = newWorkOrder.priority?.name?.toLowerCase() || '';
    const delivery = new Date(today);
    if (priority === 'high') {
      delivery.setDate(delivery.getDate() + 1);
    } else if (priority === 'medium') {
      delivery.setDate(delivery.getDate() + 3);
    } else if (priority !== 'emergency') {
      delivery.setDate(delivery.getDate() + 7);
    }

    const poGroups = {};
    const reserveParts = [];
    const warehouseBackorderParts = [];

    for (const p of updatedParts) {
      console.log('parts data', p);
      if (p.status !== 'Draft') continue;
      const hasExistingPurchaseOrder = Boolean(
        p.poLineId || p.purchaseOrderNumber || p.purchaseOrderId,
      );
      const isStockItem = p.parts?.stockItemId !== null && p.parts?.stockItemId !== undefined;

      if (isStockItem) {
        if (p.stockInfo?.inStock) {
          reserveParts.push(p);
        } else {
          warehouseBackorderParts.push(p);
        }
        continue;
      }

      if (p.source === 'PURCHASE ORDER') {
        if (hasExistingPurchaseOrder) {
          try {
            let purchaseOrderId = p.purchaseOrderId;
            if (!purchaseOrderId && p.poLineId) {
              const line = await getById('purchase-order-lines', p.poLineId);
              purchaseOrderId = line?.purchaseOrderId;
            }
            if (!purchaseOrderId) continue;
            const order = await getPurchaseOrderById(purchaseOrderId);
            const statusId = resolvePurchaseOrderStatusId(order);
            if (statusId !== 1) continue;
          } catch (err) {
            console.error('Error validating existing purchase order status', err);
            continue;
          }
        }
        if (!p.supplierId) continue;
        if (!poGroups[p.supplierId]) poGroups[p.supplierId] = [];
        poGroups[p.supplierId].push(p);
      }
    }

     const deliveryAddressLines = [
      newWorkOrder.department?.name,
      newWorkOrder.department?.deliveryAddressL1,
      newWorkOrder.department?.deliveryAddressL2,
      newWorkOrder.department?.deliveryCity,
      newWorkOrder.department?.deliveryState,
      newWorkOrder.department?.deliveryPostCode,
    ].filter(Boolean);

    const deliveryAddress = deliveryAddressLines.join('\n');

    const billingAddress = [
      newWorkOrder.department?.billingAddressL1,
      newWorkOrder.department?.billingAddressL2,
      newWorkOrder.department?.billingCity,
      newWorkOrder.department?.billingState,
      newWorkOrder.department?.billingPostcode,
    ].filter(Boolean).join(' ');

    for (const sid of Object.keys(poGroups)) {
      try {
        let po = await findOpenPurchaseOrder(
          sid,
          newWorkOrder.departmentId || newWorkOrder.department?.id,
        );
        if (!po) {
          const poPayload = {
            supplierId: sid,
            departmentId: newWorkOrder.departmentId || newWorkOrder.department?.id,
            orderDate: format(today),
            deliveryDate: format(delivery),
            deliveryAddress,
            billingAddress,
            status: 'PendingApproval',
            totalAmount: 0,
            currency: 'AUD',
            exchangeRate: 1,
            taxInclusive: 1,
            taxAmount: 0.1,
            notes: '',
            createdBy: userProfile.id,
          };
          po = await createPurchaseOrder(poPayload);
        }
        let total = Number(po.totalAmount || 0);
        const partStatus = resolvePurchaseOrderStatus(po);
        for (const part of poGroups[sid]) {
          const itemCode = part.parts.oemNumber || part.parts.partNumber;
          const qty = part.quantity || 0;
          const unit = Number(part.cost || 0);
          const lineTotal = qty * unit * 1.1;
          const line = await createPurchaseOrderLine({
            purchaseOrderId: po.id,
            itemCode,
            description: part.parts.description,
            quantity: qty,
            unitPrice: unit,
            taxRate: 0.1,
            lineTotal,
            notes: `Work Order #${newWorkOrder.workOrderNumber}`,
          });
          await update('work-order-parts', part.id, {
            status: partStatus,
            poLineId: line.id,
          });
          part.status = partStatus;
          part.poLineId = line.id;
          total += lineTotal;
        }
        await updatePurchaseOrder(po.id, { totalAmount: total });
      } catch (err) {
        console.error('Error creating purchase order', err);
      }
    }

    for (const part of reserveParts) {
      try {
        const stockItemId = part.stockItemId ?? part.parts?.stockItemId ?? part.stockInfo?.stockItemId ?? null;
        if (stockItemId) {
          await createStockItemsQuantities({
            stockItemId,
            type: 'Reserved',
            workOrderPartsId: part.id,
            quantity: -1 * Number(part.quantity || 0),
          });
        }
        await update('work-order-parts', part.id, { status: 'Reserved' });
        part.status = 'Reserved';
      } catch (err) {
        console.error('Error reserving stock', err);
      }
    }

    for (const part of warehouseBackorderParts) {
      try {
        const qty = Number(part.quantity || 0);
        const stockItemId = part.stockItemId ?? part.parts?.stockItemId ?? part.stockInfo?.stockItemId ?? null;
        if (stockItemId) {
          await createStockItemsQuantities({
            stockItemId,
            type: 'Back Order',
            workOrderPartsId: part.id,
            quantity: -1 * qty,
          });
        }
        await update('work-order-parts', part.id, { status: 'Backorder' });
        part.status = 'Backorder';
      } catch (err) {
        console.error('Error creating warehouse backorder transaction', err);
      }
    }
    setWorkOrderParts([...updatedParts]);
  };

  const handleOrderPartsPreview = () => {
    const transactions = workOrderParts
      .filter((p) => p?.status === 'Draft')
      .map((part) => ({
        id: `${part.id ?? part._clientId}`,
        checked: true,
        partNumber: part?.parts?.partNumber ?? '',
        description: part?.parts?.description ?? '',
        transactionDescription:
          part?.source === 'WAREHOUSE'
            ? `Reserve part from warehouse ${stockLookupByPartId[part.id ?? part._clientId]?.warehouses?.[0]?.warehouseName ?? ''}`.trim()
            : `Create or add part to purchase order for ${part?.supplier?.name ?? 'Supplier'}`,
      }));
    setProposedTransactions(transactions);
    setShowReviewPartOrdersModal(true);
  };

  const handleApplySelectedTransactions = async () => {
    const selectedIds = new Set(proposedTransactions.filter((t) => t.checked).map((t) => t.id));
    const retainedUnselectedDrafts = workOrderParts.filter(
      (p) => p.status === 'Draft' && !selectedIds.has(`${p.id ?? p._clientId}`),
    );
    const selectedParts = workOrderParts.filter(
      (p) => p.status !== 'Draft' || selectedIds.has(`${p.id ?? p._clientId}`),
    );
    setWorkOrderParts(selectedParts);
    await handleRequisitionParts();
    setWorkOrderParts((prev) => [...prev, ...retainedUnselectedDrafts]);
    setShowReviewPartOrdersModal(false);
  };

  useEffect(() => {
    const loadStockLookup = async () => {
      if (!editingId) return;
      const stockParts = workOrderParts.filter((part) => part?.stockItemId !== null && part?.stockItemId !== undefined);
      await Promise.all(stockParts.map(async (part) => {
        try {
          const stock = await getWarehouseStockForWorkOrder(part.stockItemId, editingId);
          setStockLookupByPartId((prev) => ({ ...prev, [part.id ?? part._clientId]: stock }));
        } catch (error) {
          setStockLookupByPartId((prev) => ({ ...prev, [part.id ?? part._clientId]: { warehouses: [] } }));
        }
      }));
    };
    loadStockLookup();
  }, [editingId, workOrderParts]);

  const handlePartCreatedFromModal = useCallback(
    ({ part, partsCost = '', supplierId, supplier, source }) => {
      if (!part || !part.id) return;

      const resolvedSource = source ?? resolvePartSource(part);
      const resolvedSupplierId = supplierId ?? part.supplierId ?? null;
      const resolvedSupplier = supplier ?? part.supplier ?? null;
      const resolvedCost =
        partsCost !== undefined && partsCost !== null && partsCost !== ''
          ? partsCost
          : part.partsCost ?? part.lastPrice ?? '';
      const optionPayload = {
        value: part.id,
        label: `${part.partNumber} – ${part.description}`.trim() || `Part ${part.id}`,
        raw: part,
      };

      setSelectedPartOption(optionPayload);
      setPartForm((prev) => ({
        ...prev,
        partsId: part.id,
        parts: part,
        source: resolvedSource,
        supplierId: resolvedSupplierId,
        supplier: resolvedSupplier,
        cost: resolvedCost,
      }));
      setPartSelectRefreshKey((prev) => prev + 1);
      setShowAddPartModal(false);
    },
    [resolvePartSource],
  );

  const handleSupplierChange = async (index, id, option) => {
    const supplierId = option?.value || null;
    setWorkOrderParts((prev) =>
      prev.map((item, i) => (i === index ? { ...item, supplierId, supplier: option ? option.raw || { id: option.value, name: option.label } : null } : item))
    );
    if (id) {
      try {
        await update('work-order-parts', id, { supplierId });
      } catch (err) {
        console.error('Failed to update supplier', err);
      }
    }
  };

  const handleExpenseCodeChange = (index, option) => {
    const rawValue = option?.value;
    const numericValue = rawValue === null || rawValue === undefined ? null : Number(rawValue);
    const resolvedValue = Number.isNaN(numericValue) ? null : numericValue;
    const matchedExpense = Array.isArray(expenseOptions)
      ? expenseOptions.find((ec) => Number(ec.id) === Number(resolvedValue))
      : null;
    setWorkOrderParts((prev) =>
      prev.map((item, i) =>
        i === index
          ? {
            ...item,
            expenseCodeId: resolvedValue,
            expenseCode: matchedExpense ? { ...matchedExpense } : null,
          }
          : item,
      ),
    );
  };

  const handleAvailabilityToggle = async (index, part, nextValue) => {
    const availableValue = nextValue ? 1 : 0;
    //const nextStatus = nextValue ? 'Available' : 'Draft';
    const previousState = workOrderParts[index];

    setWorkOrderParts((prev) =>
      prev.map((item, i) =>
        i === index
          ? {
            ...item,
            available: availableValue,
            // status: nextStatus,
          }
          : item,
      ),
    );

    if (!part?.id) return;

    try {
      await update('work-order-parts', part.id, {
        available: availableValue,
        // status: nextStatus,
      });
    } catch (err) {
      console.error('Failed to update part availability', err);
      setWorkOrderParts((prev) =>
        prev.map((item, i) => (i === index ? previousState : item)),
      );
    }
  };

  useEffect(() => {
    const loadPurchaseOrderNumbers = async () => {
      const partsToUpdate = workOrderParts.filter(
        (p) => p.source === 'PURCHASE ORDER' && p.poLineId && !p.purchaseOrderNumber,
      );
      if (partsToUpdate.length === 0) return;
      const updated = await Promise.all(
        workOrderParts.map(async (p) => {
          if (p.source === 'PURCHASE ORDER' && p.poLineId && !p.purchaseOrderNumber) {
            try {
              const line = await getById('purchase-order-lines', p.poLineId);
              if (line?.purchaseOrderId) {
                const po = await getPurchaseOrderById(line.purchaseOrderId);
                if (po?.poNumber) {
                  return {
                    ...p,
                    purchaseOrderNumber: po.poNumber,
                    purchaseOrderId: line.purchaseOrderId,
                  };
                }
              }
            } catch (err) {
              console.error('Failed to load purchase order number', err);
            }
          }
          return p;
        }),
      );
      setWorkOrderParts(updated);
    };
    loadPurchaseOrderNumbers();
  }, [workOrderParts, setWorkOrderParts]);


  const handleDeletePart = async (wp, idx) => {
    if (!window.confirm('Are you sure you want to remove this part from the work order?')) {
      return;
    }

    if (wp.poLineId) {
      try {
        const line = await getById('purchase-order-lines', wp.poLineId);
        const purchaseOrderId = line?.purchaseOrderId;
        if (purchaseOrderId) {
          const order = await getPurchaseOrderById(purchaseOrderId);
          const statusId = resolvePurchaseOrderStatusId(order);
          if ([2, 3, 4].includes(statusId)) {
            setShowCannotDeleteModal(true);
            return;
          }
          if (statusId === 1) {
            await deletePurchaseOrderLine(wp.poLineId);
          }
        }
      } catch (err) {
        console.error('Failed to validate purchase order status', err);
      }
    }

    setWorkOrderParts((prev) => prev.filter((_, i) => i !== idx));
    if (wp.id) setDeletedPartIds((prev) => [...prev, wp.id]);
  };

  const handleOpenPurchaseOrderDetails = async (wp) => {
    if (!wp) return;
    setShowPurchaseOrderDetailsModal(true);
    setIsLoadingPurchaseOrderDetails(true);
    try {
      let purchaseOrderId = wp.purchaseOrderId;
      if (!purchaseOrderId && wp.poLineId) {
        const line = await getById('purchase-order-lines', wp.poLineId);
        purchaseOrderId = line?.purchaseOrderId;
      }
      if (!purchaseOrderId) {
        setPurchaseOrderDetails(null);
        setPurchaseOrderDetailLines([]);
        return;
      }
      const [order, linesResponse] = await Promise.all([
        getPurchaseOrderById(purchaseOrderId),
        getPurchaseOrderLinesByPurchaseOrderId(purchaseOrderId),
      ]);
      const lines = Array.isArray(linesResponse?.data)
        ? linesResponse.data
        : Array.isArray(linesResponse)
          ? linesResponse
          : [];
      setPurchaseOrderDetails(order || null);
      setPurchaseOrderDetailLines(lines);
    } catch (err) {
      console.error('Failed to load purchase order details', err);
      setPurchaseOrderDetails(null);
      setPurchaseOrderDetailLines([]);
    } finally {
      setIsLoadingPurchaseOrderDetails(false);
    }
  };

  return (
    <>
      <div className="row g-3">
        <div className="col-md-12 d-flex justify-content-end gap-2 align-items-end">
          <button
            className="btn btn-outline-primary btn-sm"
            onClick={handleOrderPartsPreview}
            disabled={isWorkOrderLocked}
          >
            Order Parts
          </button>
          <div className="btn-group">
            <button
              className="btn btn-primary btn-sm"
              onClick={() => {
                setPartForm({
                  partsId: null,
                  parts: null,
                  quantity: 1,
                  source: '',
                  cost: '',
                });
                setSelectedPartOption(null);
                setPartModalErrors({});
                setPartSearchText('');
                setPartSelectRefreshKey((prev) => prev + 1);
                setShowAddWorkOrderPartModal(true);
              }}
              disabled={isWorkOrderLocked}
            >
              + Add Part
            </button>
            <button
              type="button"
              className="btn btn-primary btn-sm dropdown-toggle dropdown-toggle-split"
              data-bs-toggle="dropdown"
              aria-expanded="false"
              disabled={isWorkOrderLocked}
            >
              <span className="visually-hidden">Toggle Dropdown</span>
            </button>
            <ul className="dropdown-menu dropdown-menu-end">
              <li className="my-6">
                <button
                  className="dropdown-item py-8 d-flex align-items-center gap-2"
                  onClick={(e) => {
                    const toggleBtn = e.currentTarget
                      .closest('.btn-group')
                      ?.querySelector('.dropdown-toggle');
                    if (toggleBtn) {
                      Dropdown.getOrCreateInstance(toggleBtn).hide();
                    }
                    openBomAiModal();
                  }}
                >
                  <Icon icon="mdi:stars-outline" className="icon text-xl line-height-1" />
                  Import with AI
                </button>
              </li>
            </ul>
          </div>
          {/*<button className="btn btn-secondary btn-sm" onClick={handleConfirmMaterials}>
            Check Parts Availability
          </button>*/}
        </div>
      </div>
      <div className="table-responsive mt-3" style={{ maxHeight: 'calc(100vh - 330px)' }}>
        <table className="table bordered-table">
          <thead>
            <tr>
              <th style={{ width: '30px', textAlign: 'center' }}></th>
              <th>Part Number</th>
              <th>Description</th>
              <th>Source</th>
              <th>Supplier</th>
              <th>Expense Code</th>
              <th>Quantity</th>
              <th>Cost</th>
              <th>Received</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {workOrderParts.length === 0 && (
              <tr>
                <td colSpan="11" className="text-center py-4 rounded-0">No Parts on Work Order</td>
              </tr>
            )}
            {workOrderParts.map((wp, idx) => {
              const resolvedExpenseCodeId =
                wp.expenseCodeId ?? wp.expenseCode?.id ?? null;
              const expenseSelectValue = expenseCodeOptions.find(
                (opt) => Number(opt.value) === Number(resolvedExpenseCodeId),
              ) || null;
              const expenseDisplay = Array.isArray(expenseOptions)
                ? expenseOptions.find((ec) => Number(ec.id) === Number(resolvedExpenseCodeId))?.name || ''
                : '';
              return (
                <tr key={idx}>
                  <td style={{ width: '30px', textAlign: 'center' }}>
                    {wp.source === 'WAREHOUSE' && wp.status === 'Draft' && wp.stockInfo ? (
                      <>
                        <Icon
                          icon={wp.stockInfo.inStock ? 'mage:check-circle-fill' : 'mage:exclamation-circle-fill'}
                          style={{ position: 'relative', top: '-4px' }}
                          data-bs-toggle="tooltip"
                          data-bs-html="true"
                          title={`Stock On Hand: ${wp.stockInfo.soh} Available: ${wp.stockInfo.available} Backorder: ${wp.stockInfo.backOrder} Reserved: ${wp.stockInfo.reserved}`}
                          className={`text-xl me-4 ${wp.stockInfo.inStock ? 'text-success' : 'text-danger'}`}
                        />
                      </>
                    ) : null}
                    {wp.status === 'Reserved' ? (
                      <>
                        <Icon icon="mage:check-circle-fill" style={{ position: 'relative', top: '-4px' }} className={'text-xl me-4 text-success'} />
                      </>
                    ) : null}
                    {wp.status === 'Backorder' ? (
                      <>
                        <Icon icon="mage:check-circle-fill" style={{ position: 'relative', top: '-4px' }} className={'text-xl me-4 text-secondary'} />
                      </>
                    ) : null}
                    {wp.status === 'PendingApproval' ? (
                      <>
                        <Icon icon="mage:check-circle-fill" style={{ position: 'relative', top: '-4px' }} className={'text-xl me-4 text-warning'} />
                      </>
                    ) : null}
                    {wp.status === 'Cancelled' ? (
                      <>
                        <Icon icon="mdi:close-circle" style={{ position: 'relative', top: '-4px' }} className={'text-xl me-4 text-danger'} />
                      </>
                    ) : null}
                  </td>
                  <td style={{ width: '120px' }}>
                    <strong>{wp.parts.partNumber}</strong>
                    <div className="text-sm text-muted">{wp.parts.oemNumber}</div>
                  </td>
                  <td style={{ width: '220px' }}>{wp.parts.description}</td>
                  <td style={{ width: '120px' }}>
                    {wp.source === 'WAREHOUSE' ? (
                      <>
                        <span className="badge bg-secondary">{wp.source}</span>
                        <div className="text-xs mt-1">
                          {(stockLookupByPartId[wp.id ?? wp._clientId]?.warehouses || []).length > 0
                            ? stockLookupByPartId[wp.id ?? wp._clientId].warehouses.map((w) => (
                              <div key={`${wp.id ?? wp._clientId}-${w.warehouseId}`}>
                                {w.warehouseName}: {w.quantityAvailable} available
                              </div>
                            ))
                            : wp.stockItemId
                              ? 'No matching warehouse stock'
                              : ''}
                        </div>
                      </>
                    ) : wp.source === 'PURCHASE ORDER' && wp.purchaseOrderNumber ? (
                      <button
                        type="button"
                        className="btn btn-link p-0 text-primary"
                        onClick={() => handleOpenPurchaseOrderDetails(wp)}
                      >
                        {wp.purchaseOrderNumber}
                      </button>
                    ) : (
                      <span className="badge bg-primary">{wp.source}</span>
                    )}
                  </td>
                  <td style={{ width: '200px' }}>
                    {wp.source === 'WAREHOUSE' ? (
                      '-'
                    ) : wp.status === 'Draft' && wp.source === 'PURCHASE ORDER' ? (
                      <>
                        <AsyncSelect
                          classNamePrefix="react-select"
                          loadOptions={handleAsyncSupplierSearch}
                          defaultOptions={supplierOptions}
                          cacheOptions
                          value={wp.supplier ? { value: wp.supplier.id ?? wp.supplierId, label: wp.supplier.name } : null}
                          onChange={(opt) => handleSupplierChange(idx, wp.id, opt)}
                          placeholder="Search supplier"
                          menuPortalTarget={document.body}
                          styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                          onMenuScrollToBottom={handleSupplierMenuScroll}
                          isLoading={supplierLoading}
                        />
                      </>
                    ) : (
                      <span>{wp.supplier?.name || ''}</span>
                    )}
                  </td>
                  <td style={{ width: '180px' }}>
                    {wp.status === 'Draft' ? (
                      <Select
                        classNamePrefix="react-select"
                        options={expenseCodeOptions}
                        value={expenseSelectValue}
                        onChange={(opt) => handleExpenseCodeChange(idx, opt)}
                        placeholder="Select expense"
                        menuPortalTarget={document.body}
                        styles={menuPortalStyles}
                        isClearable
                      />
                    ) : (
                      <span>{expenseDisplay}</span>
                    )}
                  </td>
                  <td className="text-center" style={{ width: '90px' }}>
                    {wp.status === 'Draft' ? (
                      <input
                        type="number"
                        min="1"
                        value={wp.quantity ?? ''}
                        onChange={(e) => {
                          const raw = e.target.value;
                          const q = raw === '' ? '' : parseInt(raw, 10);
                          setWorkOrderParts((prev) => prev.map((item, i) => (i === idx ? { ...item, quantity: isNaN(q) ? '' : q } : item)));
                        }}
                        className="form-control form-control-sm"
                        style={{ paddingRight: '10px', paddingLeft: '10px' }}
                      />
                    ) : (
                      <span>{wp.quantity}</span>
                    )}
                  </td>
                  <td style={{ width: '100px' }}>
                    {wp.status === 'Draft' ? (
                      <>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={wp.cost ?? ''}
                          onChange={(e) => {
                            const val = e.target.value;
                            setWorkOrderParts((prev) =>
                              prev.map((item, i) =>
                                i === idx ? { ...item, cost: val } : item
                              )
                            );
                          }}
                          className={`form-control form-control-sm ${partsErrors[idx]?.cost ? 'is-invalid' : ''}`}
                          style={{ paddingRight: '10px', paddingLeft: '10px' }}
                        />
                        {partsErrors[idx]?.cost && (
                          <div className="invalid-feedback d-block">{partsErrors[idx].cost}</div>
                        )}
                      </>
                    ) : (
                      <span>{wp.cost ? Number(wp.cost).toFixed(2) : '0.00'}</span>
                    )}
                  </td>
                  <td className="text-center" style={{ width: '110px' }}>
                    <div className="form-switch d-flex justify-content-center mb-0">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        role="switch"
                        id={`parts-available-${idx}`}
                        checked={Number(wp.available) === 1}
                        disabled={Number(newWorkOrder.statusId) !== 1}
                        onChange={(e) => handleAvailabilityToggle(idx, wp, e.target.checked)}
                      />
                    </div>
                  </td>
                  <td style={{ width: '95px' }}>
                    {wp.status === 'Reserved' ? <span className="badge bg-success">{wp.status}</span> : null}
                    {wp.status === 'Available' ? <span className="badge bg-success">{wp.status}</span> : null}
                    {wp.status === 'Approved' ? <span className="badge bg-success">{wp.status}</span> : null}
                    {wp.status === 'Ordered' ? <span className="badge bg-success">{wp.status}</span> : null}
                    {wp.status === 'Backorder' ? <span className="badge bg-primary">{wp.status}</span> : null}
                    {wp.status === 'PendingApproval' ? <span className="badge bg-warning">{wp.status}</span> : null}
                    {wp.status === 'Draft' ? <span className="badge bg-secondary">{wp.status}</span> : null}
                    {wp.status === 'OnOrder' ? <span className="badge bg-primary">{wp.status}</span> : null}
                    {wp.status === 'Cancelled' ? <span className="badge bg-danger">{wp.status}</span> : null}
                  </td>
                  <td style={{ width: '80px' }}>
                    <button
                      className="w-32-px h-32-px bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center me-4"
                      onClick={() => handleDeletePart(wp, idx)}
                      disabled={isWorkOrderLocked}
                    >
                      <Icon icon="ic:baseline-delete-outline" className="icon line-height-1" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <AddPartsModal
        show={showAddWorkOrderPartModal}
        title="Add Part to Work Order"
        fields={partModalFields}
        renderField={renderPartModalField}
        onCancel={resetPartModal}
        onSave={handlePartModalSave}
      />
      <AddPartModal
        show={showAddPartModal}
        onClose={() => setShowAddPartModal(false)}
        onPartCreated={handlePartCreatedFromModal}
        initialValues={addPartInitialValues}
        selectedExpenseCodeId={selectedExpenseCodeId}
        expenseOptions={expenseOptions}
      />
      {showBomAiModal && (
        <div className="modal modal-backdrop show d-block" tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <Icon
                    className="text-xxl me-12 text-primary"
                    style={{ position: 'relative', top: '-4px' }}
                    icon="mdi:stars-outline"
                  />
                  Add Bill of Materials
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeBomAiModal}
                  disabled={bomAiLoading}
                />
              </div>
              <div className="modal-body">
                <div className="text-sm text-secondary">
                  <p>
                    Copy and Paste a bill of materials list in any format into the text area below. Make sure the
                    text contains bill of materials items with Part Number, Description, Quantity as a minimum.
                    Optionally include the Price, Pricing Date and Optional flag for each part.
                  </p>
                </div>
                {bomAiError && (
                  <div className="alert alert-danger" role="alert">
                    {bomAiError}
                  </div>
                )}
                <textarea
                  className="form-control"
                  rows={6}
                  value={bomAiText}
                  onChange={(e) => setBomAiText(e.target.value)}
                  disabled={bomAiLoading}
                  placeholder="Paste a list of parts in any format"
                />
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={closeBomAiModal} disabled={bomAiLoading}>
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleImportBomWithAi}
                  disabled={bomAiLoading || !bomAiText.trim()}
                >
                  {bomAiLoading ? 'Adding...' : 'Add with AI'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showMissingBomModal && (
        <div className="modal modal-backdrop show d-block" tabIndex="-1">
          <div className="modal-dialog modal-xl">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Add New Parts to Catalogue</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    if (missingBomSaving) return;
                    setShowMissingBomModal(false);
                    setMissingBomParts([]);
                    setMissingBomError('');
                  }}
                  disabled={missingBomSaving}
                />
              </div>
              <div className="modal-body">
                {missingBomError && (
                  <div className="alert alert-danger" role="alert">
                    {missingBomError}
                  </div>
                )}
                <div className="row g-3 mb-3">
                  <div className="col-md-6">
                    <label className="form-label">Source of Supply</label>
                    <select
                      className="form-select"
                      value={bomGlobalSourceId}
                      onChange={(e) => handleGlobalBomSourceChange(e.target.value)}
                    >
                      <option value="">Select a source of supply</option>
                      {bomManufacturerOptions.map((manufacturer) => (
                        <option key={manufacturer.id} value={manufacturer.id}>
                          {manufacturer.manufacturer}
                        </option>
                      ))}
                    </select>
                    <div className="form-check mt-16">
                      <input
                        className="form-check-input me-8"
                        style={{ position: "relative", top: "2px" }}
                        type="checkbox"
                        id="bom-source-apply-all"
                        checked={bomApplySourceToAll}
                        onChange={(e) => handleApplySourceToAllChange(e.target.checked)}
                      />
                      <label className="form-check-label" htmlFor="bom-source-apply-all">
                        Apply to all parts
                      </label>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Preferred Supplier</label>
                    <select
                      className="form-select"
                      value={bomGlobalSupplierId}
                      onChange={(e) => handleGlobalBomSupplierChange(e.target.value)}
                    >
                      <option value="">Select a supplier</option>
                      {bomSupplierOptions.map((supplier) => (
                        <option key={supplier.id} value={supplier.id}>
                          {supplier.name}
                        </option>
                      ))}
                    </select>
                    <div className="form-check mt-16">
                      <input
                        className="form-check-input me-8"
                        style={{ position: "relative", top: "2px" }}
                        type="checkbox"
                        id="bom-supplier-apply-all"
                        checked={bomApplySupplierToAll}
                        onChange={(e) => handleApplySupplierToAllChange(e.target.checked)}
                      />
                      <label className="form-check-label" htmlFor="bom-supplier-apply-all">
                        Apply to all parts
                      </label>
                    </div>
                  </div>
                </div>
                <div className="table-responsive"
                style={{ maxHeight: 'calc(100vh - 380px)'}}>
                  <table className="table bordered-table align-middle">
                    <thead>
                      <tr>
                        <th>Part #</th>
                        <th>Description</th>
                        <th>Qty</th>
                        <th>Price</th>
                        <th>Source of Supply</th>
                        <th>Preferred Supplier</th>
                      </tr>
                    </thead>
                    <tbody>
                      {missingBomParts.map((part, index) => (
                        <tr key={`${part.partNumber}-${index}`}>
                          <td>{part.partNumber}</td>
                          <td>{part.partDescription}</td>
                          <td>{part.quantity}</td>
                          <td>{part.partsPrice}</td>
                          <td>
                            <select
                              className="form-select"
                              value={part.sourceOfSupplyId}
                              onChange={(e) =>
                                handleMissingBomUpdate(index, { sourceOfSupplyId: e.target.value })
                              }
                            >
                              <option value="">Select source</option>
                              {bomManufacturerOptions.map((manufacturer) => (
                                <option key={manufacturer.id} value={manufacturer.id}>
                                  {manufacturer.manufacturer}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td>
                            <select
                              className="form-select"
                              value={part.supplierId}
                              onChange={(e) =>
                                handleMissingBomUpdate(index, { supplierId: e.target.value })
                              }
                            >
                              <option value="">Select supplier</option>
                              {bomSupplierOptions.map((supplier) => (
                                <option key={supplier.id} value={supplier.id}>
                                  {supplier.name}
                                </option>
                              ))}
                            </select>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    if (missingBomSaving) return;
                    setShowMissingBomModal(false);
                    setMissingBomParts([]);
                    setMissingBomError('');
                  }}
                  disabled={missingBomSaving}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleSaveMissingBomParts}
                  disabled={missingBomSaving}
                >
                  {missingBomSaving ? 'Saving...' : 'Add to Parts'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showMissingSupplierModal && (
        <>
          <div className="modal-backdrop fade show"></div>
          <div className="modal fade show d-block" tabIndex="-1">
            <div className="modal-dialog" style={{ top: '20vh' }}>
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Missing Supplier</h5>
                </div>
                <div className="modal-body">
                  Some Parts do not have a supplier. Do you still want to raise purchase orders for the other parts?
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowMissingSupplierModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => {
                      setShowMissingSupplierModal(false);
                      handleRequisitionParts({ ignoreMissingSuppliers: true });
                    }}
                  >
                    OK
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {showReviewPartOrdersModal && (
        <div className="modal d-block" tabIndex="-1" role="dialog">
          <div className="modal-dialog modal-lg" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Review Part Orders</h5>
                <button type="button" className="btn-close" onClick={() => setShowReviewPartOrdersModal(false)} />
              </div>
              <div className="modal-body">
                {proposedTransactions.map((transaction, index) => (
                  <div key={transaction.id} className="d-flex align-items-start gap-2 mb-2">
                    <input
                      type="checkbox"
                      checked={transaction.checked}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setProposedTransactions((prev) => prev.map((t, i) => (i === index ? { ...t, checked } : t)));
                      }}
                    />
                    <div>
                      <div><strong>{transaction.partNumber}</strong> - {transaction.description}</div>
                      <div className="text-muted">{transaction.transactionDescription}</div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowReviewPartOrdersModal(false)}>Cancel</button>
                <button className="btn btn-primary" onClick={handleApplySelectedTransactions}>OK</button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showCannotDeleteModal && (
        <>
          <div className="modal-backdrop fade show"></div>
          <div className="modal fade show d-block" tabIndex="-1">
            <div className="modal-dialog" style={{ top: '20vh' }}>
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Unable to Delete Part</h5>
                </div>
                <div className="modal-body">
                  This parts has been ordered and cannot be deleted from the Work Order.
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => setShowCannotDeleteModal(false)}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
      {showPurchaseOrderDetailsModal && (
        <>
          <div className="modal-backdrop fade show"></div>
          <div className="modal fade show d-block" tabIndex="-1">
            <div className="modal-dialog modal-xl" style={{ top: '5vh' }}>
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Purchase Order Details</h5>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Close"
                    onClick={() => setShowPurchaseOrderDetailsModal(false)}
                  ></button>
                </div>
                <div className="modal-body">
                  {isLoadingPurchaseOrderDetails ? (
                    <div>Loading purchase order details...</div>
                  ) : (
                    <PurchaseOrderPrintView
                      order={purchaseOrderDetails}
                      orderLines={purchaseOrderDetailLines}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default WorkOrderPartsTab;
